import { GridLegacy, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconHome } from '@tabler/icons-react';
import UserAvatar from 'ui-component/extended/Avatars/UserAvatar';
import { User } from 'types/user';
import { FC, memo } from 'react';
import _ from 'lodash';
import { useGetUserQuery } from 'store/slices/apiV1/user';
import { isCofactrUser } from 'utils/userPermissions';
import { useSelector } from 'store';
import CopyClipboard from 'ui-component/extended/CopyClipboard';

export enum UserInfoSize {
  SM = 'sm',
  MD = 'md',
}

interface UserInfoSectionProps {
  user:
    | Pick<
        User,
        'firstName' | 'lastName' | 'title' | 'email' | 'isStaff' | 'image'
      >
    | null
    | undefined;
  subtitle?: string;
  size?: UserInfoSize;
  automation?: boolean;
  showEmail?: boolean;
}

const UserInfoSection: FC<UserInfoSectionProps> = ({
  user,
  subtitle,
  size,
  automation,
  showEmail = false,
}) => {
  const theme = useTheme();
  const { data: currentUser } = useGetUserQuery();
  const { viewAsStaff } = useSelector((state) => state.user);

  const obscureCofactrUser =
    user?.isStaff && !isCofactrUser({ user: currentUser, viewAsStaff });

  return (
    <GridLegacy
      container
      columnSpacing={1.5}
      justifyContent="flex-start"
      alignItems="center"
    >
      <GridLegacy item xs="auto">
        <UserAvatar
          user={user || undefined}
          sx={{
            ...theme.typography.mediumAvatar,
          }}
          size={size}
          automation={automation}
          obscureCofactrUser={obscureCofactrUser}
        />
      </GridLegacy>
      <GridLegacy item xs sx={{ overflow: 'hidden' }}>
        {automation && !user && (
          <Typography align="left" variant="subtitle1" noWrap>
            Cofactr Platform
          </Typography>
        )}
        {!!user && obscureCofactrUser && (
          <Typography align="left" variant="subtitle1" noWrap>
            Cofactr Success
          </Typography>
        )}
        {!!user && !obscureCofactrUser && (
          <Typography align="left" variant="subtitle1" noWrap>
            {user?.firstName || ''} {user?.lastName || ''}
            {'   '}
            {_.get(user, 'isInternal', false) && (
              <IconHome color={theme.palette.primary.dark} width={12} />
            )}
          </Typography>
        )}
        <Typography align="left" variant="subtitle2" noWrap>
          {subtitle || (obscureCofactrUser ? '' : user?.title) || '-'}
        </Typography>
        {showEmail && user?.email && !obscureCofactrUser && (
          <Stack direction="row" alignItems="center">
            <Typography align="left" variant="subtitle2" noWrap>
              {user?.email}
            </Typography>
            <CopyClipboard label="Email" input={user?.email} iconSize={16} />
          </Stack>
        )}
      </GridLegacy>
    </GridLegacy>
  );
};

export default memo(UserInfoSection);
